import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import permissionsService, { UserPermissions } from '../services/permissions.service';
import { useVisibilityAwareInterval } from '../hooks/useVisibilityAwareInterval';

const PERMISSIONS_CACHE_KEY = 'gwi360_permissions_cache';

/**
 * Max age of the localStorage snapshot. It exists to render the shell instantly
 * while a fresh copy loads, so it only has to survive a reload — not a shift.
 * Without a bound, a snapshot whose background refresh kept failing stayed
 * authoritative until the user signed out, which is what made permission
 * changes look like they needed a new session.
 */
const PERMISSIONS_CACHE_MAX_AGE_MS = 8 * 60 * 60 * 1000;

/** How often an open tab re-reads permissions, so changes land without a reload. */
const PERMISSIONS_REFRESH_MS = 5 * 60 * 1000;

interface CachedPermissions {
  permissions: UserPermissions;
  timestamp: number;
  userId: string;
}

interface PermissionContextType {
  permissions: UserPermissions | null;
  loading: boolean;
  error: string | null;
  hasAccess: (route: string) => boolean;
  isInGroups: (groupIds: string[]) => boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

// Helper to get cached permissions from localStorage
const getCachedPermissions = (userId: string): UserPermissions | null => {
  try {
    const cached = localStorage.getItem(PERMISSIONS_CACHE_KEY);
    if (!cached) return null;

    const parsed: CachedPermissions = JSON.parse(cached);

    // Only use cache if it's for the same user
    if (parsed.userId !== userId) return null;

    // Expired snapshots are dropped so a stale copy can never outlive a failing
    // refresh. A missing timestamp is treated as expired (pre-TTL cache entry).
    if (!parsed.timestamp || Date.now() - parsed.timestamp > PERMISSIONS_CACHE_MAX_AGE_MS) {
      localStorage.removeItem(PERMISSIONS_CACHE_KEY);
      return null;
    }

    return parsed.permissions;
  } catch {
    return null;
  }
};

// Helper to save permissions to localStorage
const setCachedPermissions = (permissions: UserPermissions, userId: string): void => {
  try {
    const cache: CachedPermissions = {
      permissions,
      timestamp: Date.now(),
      userId,
    };
    localStorage.setItem(PERMISSIONS_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // localStorage might be full or disabled, ignore
  }
};

// Helper to check if permissions changed
const permissionsChanged = (a: UserPermissions | null, b: UserPermissions | null): boolean => {
  if (!a || !b) return true;
  if (a.user_id !== b.user_id) return true;
  if (a.groups.length !== b.groups.length) return true;
  if (a.accessible_routes.length !== b.accessible_routes.length) return true;
  if (!a.groups.every(g => b.groups.includes(g))) return true;
  if (!a.accessible_routes.every(r => b.accessible_routes.includes(r))) return true;
  if (a.home_region !== b.home_region) return true;
  if (a.home_region_as_default !== b.home_region_as_default) return true;
  if (a.default_yard !== b.default_yard) return true;
  if (a.v1_username !== b.v1_username) return true;
  return false;
};

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { accounts } = useMsal();
  const [permissions, setPermissions] = useState<UserPermissions | null>(() => {
    // Initialize from cache immediately for fast load
    if (accounts && accounts.length > 0) {
      const userId = accounts[0]?.localAccountId || '';
      return getCachedPermissions(userId);
    }
    return null;
  });
  const [loading, setLoading] = useState<boolean>(() => {
    // If we have cached permissions, don't show loading state
    if (accounts && accounts.length > 0) {
      const userId = accounts[0]?.localAccountId || '';
      return !getCachedPermissions(userId);
    }
    return true;
  });
  const [error, setError] = useState<string | null>(null);
  const fetchInProgress = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchPermissions = async (isBackgroundRefresh = false) => {
    if (!accounts || accounts.length === 0) {
      setLoading(false);
      return;
    }

    // Prevent duplicate fetches
    if (fetchInProgress.current) return;
    fetchInProgress.current = true;

    // Abort any previous request
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      // Only show loading if not a background refresh and no cached data
      if (!isBackgroundRefresh && !permissions) {
        setLoading(true);
      }
      setError(null);

      // Get the user's permissions (sync is handled automatically by backend)
      const userPermissions = await permissionsService.getUserPermissions(controller.signal);

      // Always re-write the snapshot so a successful revalidation refreshes its
      // timestamp; only touch state when something actually changed, to avoid
      // re-rendering every consumer on each background refresh.
      setCachedPermissions(userPermissions, accounts[0]?.localAccountId || '');
      if (permissionsChanged(permissions, userPermissions)) {
        setPermissions(userPermissions);
      }
    } catch (err: any) {
      // Ignore abort errors - expected when we cancel stale requests
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        return;
      }
      // Surfaced in the console (not the UI) so a silently failing refresh is
      // diagnosable — the stale snapshot below is otherwise invisible.
      console.warn('Failed to refresh user permissions', err?.response?.status ?? err);

      // Only set empty permissions if we don't have cached ones
      if (!permissions) {
        const emptyPermissions = {
          user_id: accounts[0]?.localAccountId || '',
          user_email: accounts[0]?.username || '',
          groups: [],
          accessible_routes: [],
        };
        setPermissions(emptyPermissions);
      }
      setError(null); // Clear error so it doesn't show to user
    } finally {
      setLoading(false);
      fetchInProgress.current = false;
    }
  };

  const refreshPermissions = async () => {
    await fetchPermissions(false);
  };

  // Stale-while-revalidate: use cache immediately, fetch fresh in background
  useEffect(() => {
    if (accounts && accounts.length > 0) {
      const userId = accounts[0]?.localAccountId || '';
      const cached = getCachedPermissions(userId);

      if (cached) {
        // We have cache - set it immediately and fetch fresh in background
        setPermissions(cached);
        setLoading(false);
        // Background refresh (non-blocking)
        fetchPermissions(true);
      } else {
        // No cache - fetch and show loading
        fetchPermissions(false);
      }
    } else {
      setPermissions(null);
      setLoading(false);
      // Clear cache on logout
      localStorage.removeItem(PERMISSIONS_CACHE_KEY);
    }

    // Cleanup: abort any in-flight request on unmount or dependency change
    return () => {
      abortControllerRef.current?.abort();
      fetchInProgress.current = false;
    };
  }, [accounts]);

  // Revalidate while the tab stays open (paused when hidden, fires on return), so
  // a granted or revoked page shows up without the user reloading or signing out.
  useVisibilityAwareInterval(
    () => { fetchPermissions(true); },
    accounts && accounts.length > 0 ? PERMISSIONS_REFRESH_MS : null,
  );

  const hasAccess = (route: string): boolean => {
    if (!permissions) return false;

    // Global admins - always have full access
    const GLOBAL_ADMINS = ['john.duplechain@gwii.com'];
    if (permissions.user_email && GLOBAL_ADMINS.includes(permissions.user_email.toLowerCase())) {
      return true;
    }

    // Check if user has any accessible routes
    // If they have routes, check if this specific route is accessible
    // If they have no routes AND no groups, allow access (permissions not configured yet)
    // If they have groups but no routes, they have no access (permissions are configured, they just don't have access)
    if (permissions.accessible_routes.length === 0) {
      // If user has groups but no accessible routes, deny access (they're in groups but haven't been granted permissions)
      if (permissions.groups.length > 0) {
        return false;
      }
      // If user has no groups and no routes, allow access (permissions system not configured)
      return true;
    }

    return permissions.accessible_routes.includes(route);
  };

  const isInGroups = (groupIds: string[]): boolean => {
    if (!permissions) return false;

    // Global admins - always in all groups
    const GLOBAL_ADMINS = ['john.duplechain@gwii.com'];
    if (permissions.user_email && GLOBAL_ADMINS.includes(permissions.user_email.toLowerCase())) {
      return true;
    }

    return groupIds.some(groupId => permissions.groups.includes(groupId));
  };

  return (
    <PermissionContext.Provider
      value={{
        permissions,
        loading,
        error,
        hasAccess,
        isInGroups,
        refreshPermissions,
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

/**
 * Non-throwing variant for components that should degrade gracefully when
 * rendered outside a PermissionProvider (e.g. tests, isolated previews).
 * Returns `undefined` when no provider is in the tree.
 */
export const useTryPermissions = (): PermissionContextType | undefined => {
  return useContext(PermissionContext);
};
